import React, { useMemo } from "react";
import { Play, Lock } from "lucide-react";
import { Course, CourseProgress } from "./types";
import { formatDuration, getYoutubeThumbnail } from "./utils";

interface CourseCardProps {
  course: Course;
  moduleTitle: string;
  moduleIndex: number;
  progressData: CourseProgress[];
  openVideo: (moduleTitle: string, course: Course) => void;
}

// Création d'un composant mémoïsé pour éviter les rendus inutiles
const CourseCard: React.FC<CourseCardProps> = React.memo(
  ({ course, moduleTitle, moduleIndex, progressData, openVideo }) => {
    // Trouver la progression une seule fois et la mémoriser
    const courseProgress = useMemo(() => {
      // Log pour le débogage
      console.log("CourseCard - course:", {
        courseId: course.id,
        title: course.title,
        locked: course.locked,
        completed: course.completed,
      });
      console.log("CourseCard - progressData items:", progressData.length);

      // Vérifier si progressData est un tableau valide
      if (
        !progressData ||
        !Array.isArray(progressData) ||
        progressData.length === 0
      ) {
        console.log(`Aucune donnée de progression pour le cours ${course.id}`);
        // Utiliser les infos du cours lui-même si disponibles
        if (
          typeof course.completed !== "undefined" ||
          typeof course.progression !== "undefined"
        ) {
          console.log(
            `Utilisation des données de progression du cours lui-même:`,
            {
              completed: course.completed,
              progression: course.progression,
            }
          );
          return {
            id: `generated-${course.id}`,
            userId: "",
            courseId: course.id,
            progression:
              typeof course.progression === "number"
                ? course.progression
                : course.completed
                ? 100
                : 0,
            completed: Boolean(course.completed),
            lastWatchedAt: new Date().toISOString(),
          };
        }
        return null;
      }

      // S'assurer que course.id est une chaîne pour la comparaison
      const courseId = String(course.id);

      // Trouver la progression qui correspond à l'ID du cours actuel
      const progress = progressData.find(
        (p) => String(p.courseId) === courseId
      );

      if (!progress) {
        console.log(`Progression non trouvée pour le cours ${courseId}`);
        // Utiliser les infos du cours lui-même si disponibles
        if (
          typeof course.completed !== "undefined" ||
          typeof course.progression !== "undefined"
        ) {
          console.log(
            `Utilisation des données de progression du cours lui-même après recherche échouée:`,
            {
              completed: course.completed,
              progression: course.progression,
            }
          );
          return {
            id: `generated-${course.id}`,
            userId: "",
            courseId: course.id,
            progression:
              typeof course.progression === "number"
                ? course.progression
                : course.completed
                ? 100
                : 0,
            completed: Boolean(course.completed),
            lastWatchedAt: new Date().toISOString(),
          };
        }
      } else {
        console.log("CourseCard - progression trouvée:", progress);
      }

      return progress || null;
    }, [
      progressData,
      course.id,
      course.completed,
      course.progression,
      course.locked,
      course.title,
    ]);

    // Calculer les valeurs dérivées une seule fois
    const progressPercentage = useMemo(() => {
      if (course.completed) return 100;
      if (!courseProgress) return 0;
      return typeof courseProgress.progression === "number"
        ? Math.round(courseProgress.progression)
        : 0;
    }, [courseProgress, course.completed]);

    const lastActivityDate = useMemo(() => {
      if (!courseProgress?.lastWatchedAt) return "Jamais";
      try {
        return new Date(courseProgress.lastWatchedAt).toLocaleDateString(
          "fr-FR"
        );
      } catch {
        return "Jamais";
      }
    }, [courseProgress?.lastWatchedAt]);

    // Optimisation de l'affichage du badge de progression
    const progressBadge = useMemo(() => {
      console.log(
        "Calculer progressBadge pour",
        course.title,
        "courseProgress:",
        courseProgress,
        "completed:",
        course.completed
      );

      // Si le cours est marqué comme complété directement
      if (course.completed) {
        return (
          <div className="bg-white/80 rounded-lg px-2 py-1">
            <span className="text-green-600 text-xs font-bold">Complété</span>
          </div>
        );
      }

      // Si on a des données de progression
      if (courseProgress) {
        if (courseProgress.completed) {
          return (
            <div className="bg-white/80 rounded-lg px-2 py-1">
              <span className="text-green-600 text-xs font-bold">Complété</span>
            </div>
          );
        } else if (progressPercentage > 0) {
          return (
            <div className="bg-white/80 rounded-lg px-2 py-1">
              <span className="text-orange-500 text-xs font-bold">
                {progressPercentage}%
              </span>
            </div>
          );
        }
      }

      return null;
    }, [courseProgress, progressPercentage, course.completed, course.title]);

    // Handler for opening video - optimized to avoid inline function creation
    const handleOpenVideo = () => {
      openVideo(moduleTitle, course);
    };

    // Get thumbnail URL once
    const thumbnailUrl = useMemo(
      () => getYoutubeThumbnail(course.videoUrl),
      [course.videoUrl]
    );

    return (
      <div
        className={`bg-white rounded-lg overflow-hidden shadow-md transform transition-all duration-300 ${
          course.locked
            ? "opacity-70 pointer-events-none"
            : "hover:scale-105 hover:shadow-xl"
        }`}
      >
        <div className="relative group">
          <div className="aspect-video bg-gray-200 relative overflow-hidden">
            {course.locked ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-10">
                <Lock size={40} className="text-white mb-2" />
                <p className="text-white text-center px-4 text-sm">
                  {moduleIndex === 0 && course.order === 1
                    ? "Ce cours est verrouillé"
                    : "Terminez les capsules précédentes pour débloquer"}
                </p>
              </div>
            ) : (
              <div
                className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 z-10"
                onClick={handleOpenVideo}
              >
                <div className="bg-orange-500 bg-opacity-80 rounded-full p-3 transform scale-0 group-hover:scale-100 transition-transform duration-300 ease-in-out">
                  <Play size={28} className="text-white" />
                </div>
              </div>
            )}
            {/* Badge de progression */}
            {!course.locked && (
              <div className="absolute top-2 right-2 z-10">
                {progressBadge || (
                  <div className="bg-red-500/80 rounded-lg px-2 py-1">
                    <span className="text-white text-xs font-bold">TEST</span>
                  </div>
                )}
              </div>
            )}
            <img
              src={thumbnailUrl}
              alt={`Cours ${course.title}`}
              loading="lazy"
              className={`w-full h-full object-cover ${
                !course.locked &&
                "transform group-hover:scale-110 transition-all duration-500"
              }`}
            />
            <div className="absolute bottom-2 left-2 bg-black/70 text-white rounded px-2 py-0.5">
              <span className="text-white text-xs font-medium">
                {formatDuration(course.time)}
              </span>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center mb-2 justify-between">
              <span className="text-xs font-semibold bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                Capsule {course.order}
              </span>
              <div className="flex items-center space-x-1">
                <span className="text-xs font-medium bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                  {formatDuration(course.time)}
                </span>
              </div>
            </div>
            <h3 className="font-semibold mb-1 text-gray-800 truncate">
              {course.title}
            </h3>
            <p className="text-gray-600 text-sm line-clamp-2 h-10">
              {course.description}
            </p>

            {/* Infos de progression détaillées (version optimisée) */}
            {courseProgress && (
              <div className="mt-2 text-xs border-t pt-2 border-gray-100 text-gray-600">
                <div className="flex justify-between mb-1">
                  <span>Progression:</span>
                  <span className="font-medium">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                  <div
                    className="bg-orange-500 h-1.5 rounded-full"
                    style={{
                      width: `${progressPercentage}%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Dernière activité:</span>
                  <span>{lastActivityDate}</span>
                </div>
              </div>
            )}

            {!course.locked && (
              <button
                onClick={handleOpenVideo}
                className="mt-3 text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center transition-colors duration-200"
              >
                {course.completed ? "Revoir" : "Regarder maintenant"}
                <Play size={14} className="ml-1" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default CourseCard;
